/*
  adg-bottom-inset — the one place that decides how high off the bottom of the
  screen our floating things sit.

  WHY THIS EXISTS
  Everything docked at the bottom used to carry a hand-measured number:
  .byo-submit at bottom:0, the live-preview dock at bottom:92px (that is 70 for
  the submit bar plus a 22 gap), the to-top pill at 88 / 261 / 26 depending on
  the page. Each number was measured once, against the others, on one page, on
  one day. Change any one thing and the rest are silently wrong.

  That is what buried the BYO Add to Cart button. byo-builder.css even carried
  a comment saying Shopify's preview banner "is not present in current
  Shopify dev-store UI" — it is present again, 68px tall, sitting exactly on
  top of the button. The same thing happens with a cookie banner, a support
  chat bubble, or anything else a third party decides to dock down there, and
  fighting them on z-index is a losing game: they use 999999 and we cannot
  out-bid an arms race we do not control.

  So do not overlap them. Measure what is actually at the bottom of the
  viewport right now and stack above it.

  WHAT IT PUBLISHES  (on :root, all in px, all live)

    --adg-inset-0   top edge of the foreign junk. Where OUR stack starts.
    --adg-inset-1   above the submit bar    (inset-0 + its height + gap)
    --adg-inset-2   above the preview dock  (inset-1 + its height + gap)

  Each layer is "the floor for whatever sits above me", so a consumer never
  needs to know what is below it — only which layer it belongs to. Adding a
  fourth floating thing later means adding --adg-inset-3, not re-measuring
  three other files.

  WHAT COUNTS AS FOREIGN
  Fixed or sticky, visibly painted, at least 16px tall, and docked within 8px
  of the viewport bottom. It must also span most of the width: a full-width
  bar genuinely steals that band of screen, but a little corner chat bubble
  does not, and lifting the entire UI for one would be worse than the overlap.
  Anything of ours is skipped by [data-adg-bottom] — see OWNED below.

  Recomputes on resize, scroll (sticky bars move), and DOM mutation, so a
  cookie banner that appears late is picked up and dismissing it drops the
  inset back to zero. All of it is rAF-debounced to one pass per frame.
*/
(function () {
  'use strict';

  // Ours. Never treat these as obstructions — they ARE the stack.
  var OWNED = '[data-adg-bottom], .byo-submit, .adg-totop, .byo-tray';

  var GAP = 22;          // breathing room between stacked layers
  var MIN_HEIGHT = 16;   // shorter than this is a hairline, not a bar
  var MIN_WIDTH_RATIO = 0.55;
  var DOCK_TOLERANCE = 8; // "docked" means within this of the viewport bottom

  var root = document.documentElement;
  var queued = false;
  var last = { 0: -1, 1: -1, 2: -1 };

  // The preview dock lives in a shadow root, so it cannot be measured from
  // here. It reports its own height instead — see adg-preview-shell.js.
  var dockHeight = 0;

  function foreignInset() {
    var vh = window.innerHeight;
    var vw = window.innerWidth;
    var inset = 0;
    var nodes = document.body ? document.body.querySelectorAll('*') : [];

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest && el.closest(OWNED)) continue;

      var cs;
      try { cs = getComputedStyle(el); } catch (e) { continue; }
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      if (parseFloat(cs.opacity) < 0.05) continue;

      var r = el.getBoundingClientRect();
      if (r.height < MIN_HEIGHT) continue;
      if (r.width < vw * MIN_WIDTH_RATIO) continue;      // corner widget, ignore
      if (r.bottom < vh - DOCK_TOLERANCE) continue;      // floating, not docked
      if (r.top > vh) continue;                          // off-screen below

      var occupies = vh - r.top;
      if (occupies > inset) inset = occupies;
    }
    return Math.max(0, Math.round(inset));
  }

  function ownHeight(sel) {
    var el = document.querySelector(sel);
    if (!el) return 0;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
    var h = el.getBoundingClientRect().height;
    return h > 1 ? Math.round(h) : 0;
  }

  function measure() {
    queued = false;

    var i0 = foreignInset();

    var submit = ownHeight('.byo-submit');
    var i1 = i0 + (submit ? submit + GAP : 0);
    var i2 = i1 + (dockHeight ? dockHeight + GAP : 0);

    set(0, i0); set(1, i1); set(2, i2);

    // The live-preview dock measures 118x307 — much taller than the 157 an old
    // comment claimed, which is why the pill's hardcoded 261px was landing
    // inside it. Stacking above a 307px dock would strand the pill a third of
    // the way up the screen, so when the dock is out we move the pill to the
    // other corner instead, where nothing is docked at all.
    root.toggleAttribute('data-adg-dock', dockHeight > 0);
  }

  function set(layer, px) {
    if (last[layer] === px) return;       // don't churn style on every scroll
    last[layer] = px;
    root.style.setProperty('--adg-inset-' + layer, px + 'px');
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  }

  // Let the shadow-DOM dock tell us how tall it is.
  window.adgSetDockHeight = function (px) {
    var n = Math.max(0, Math.round(px || 0));
    if (n === dockHeight) return;
    dockHeight = n;
    schedule();
  };

  schedule();
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('load', schedule);
  document.addEventListener('DOMContentLoaded', schedule);

  // A cookie banner or chat widget injected after load, or dismissed later.
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  }
  // The submit bar changes height when its label wraps on a phone.
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(schedule);
    var watch = document.querySelector('.byo-submit');
    if (watch) ro.observe(watch);
    document.addEventListener('DOMContentLoaded', function () {
      var late = document.querySelector('.byo-submit');
      if (late && late !== watch) ro.observe(late);
    });
  }
})();
